/**
 * PageView Engine - Fase 1 Core Engine
 * Capturar cada navegação: URL, path, title, scroll máximo, duração, referrer.
 * Intercepta SPA: history.pushState, history.replaceState, popstate.
 */

import type { PageViewRecord } from '../types';

const getMaxScrollPercent = (): number => {
  if (typeof window === 'undefined' || typeof document === 'undefined')
    return 0;
  const doc = document.documentElement;
  const bodyScroll = document.body ? document.body.scrollHeight : 0;
  const scrollHeight = Math.max(doc.scrollHeight, bodyScroll);
  const clientHeight = doc.clientHeight;
  if (scrollHeight <= clientHeight) return 100;
  const maxScroll = scrollHeight - clientHeight;
  const current = window.scrollY || window.pageYOffset;
  return Math.min(100, Math.round((current / maxScroll) * 100));
};

export type PageViewCallback = (record: PageViewRecord) => void;

let currentPageStart = 0;
let currentPageUrl = '';
let maxScrollSeen = 0;
let scrollListener: (() => void) | null = null;
let callback: PageViewCallback | null = null;
let isSpyInstalled = false;

function captureCurrentState(): Omit<
  PageViewRecord,
  'maxScrollPercent' | 'durationMs'
> {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const title = typeof document !== 'undefined' ? document.title : '';
  const ref = typeof document !== 'undefined' ? document.referrer : '';
  return { url, path, title, referrer: ref, timestamp: Date.now() };
}

function resetPageTrackingState(): void {
  const state = captureCurrentState();
  currentPageUrl = state.url;
  currentPageStart = Date.now();
  maxScrollSeen = getMaxScrollPercent();

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    if (scrollListener) {
      window.removeEventListener('scroll', scrollListener);
    }
    scrollListener = () => {
      maxScrollSeen = Math.max(maxScrollSeen, getMaxScrollPercent());
    };
    window.addEventListener('scroll', scrollListener);
  }
}

/** Dispara PAGE_VIEW para a URL atual (início da visualização). */
function emitPageViewAtStart(durationMs = 0): void {
  if (!callback) return;
  const state = captureCurrentState();
  const record: PageViewRecord = {
    url: state.url,
    path: state.path,
    title: state.title,
    referrer: state.referrer,
    maxScrollPercent: maxScrollSeen || getMaxScrollPercent(),
    durationMs,
    timestamp: Date.now(),
  };
  callback(record);
}

function onUrlChange(): void {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  if (url && url !== currentPageUrl) {
    resetPageTrackingState();
    emitPageViewAtStart(0);
  }
}

function installHistorySpy(): void {
  if (typeof window === 'undefined' || isSpyInstalled) return;
  isSpyInstalled = true;

  const origPushState = history.pushState;
  const origReplaceState = history.replaceState;

  history.pushState = function (...args) {
    origPushState.apply(this, args);
    onUrlChange();
  };
  history.replaceState = function (...args) {
    origReplaceState.apply(this, args);
    onUrlChange();
  };

  window.addEventListener('popstate', onUrlChange);
}

/**
 * Registra o callback que receberá cada PageViewRecord (incluindo ao mudar de página em SPA e ao sair).
 * Intercepta pushState, replaceState e popstate.
 */
export function setPageViewCallback(fn: PageViewCallback): void {
  callback = fn;
  installHistorySpy();
  // Inicializa estado da primeira página
  const state = captureCurrentState();
  currentPageUrl = state.url;
  currentPageStart = Date.now();
  maxScrollSeen = getMaxScrollPercent();
  if (scrollListener) {
    if (typeof window !== 'undefined')
      window.removeEventListener('scroll', scrollListener);
    scrollListener = null;
  }
  if (typeof window !== 'undefined') {
    scrollListener = () => {
      maxScrollSeen = Math.max(maxScrollSeen, getMaxScrollPercent());
    };
    window.addEventListener('scroll', scrollListener);
  }
  // Estado da primeira página fica armazenado aqui; o disparo imediato fica a cargo de quem chama (ex.: tracker).
}

/**
 * Remove o callback e para de interceptar navegação.
 */
export function clearPageViewCallback(): void {
  callback = null;
  if (scrollListener && typeof window !== 'undefined') {
    window.removeEventListener('scroll', scrollListener);
    scrollListener = null;
  }
  currentPageStart = 0;
  currentPageUrl = '';
  maxScrollSeen = 0;
}

/**
 * Registro manual de uma visualização de página (ex: carga inicial ou navegação server-side).
 * Retorna o registro com scroll e duração opcionais.
 */
export function trackPageView(
  overrides?: Partial<PageViewRecord>
): PageViewRecord {
  const now = Date.now();
  const durationMs = currentPageStart ? now - currentPageStart : undefined;
  const record: PageViewRecord = {
    ...captureCurrentState(),
    maxScrollPercent: maxScrollSeen || getMaxScrollPercent(),
    durationMs,
    ...overrides,
  };
  if (callback) callback(record);
  resetPageTrackingState();
  return record;
}

/**
 * Retorna o estado atual da página (para uso externo, ex: enviar no próximo evento).
 */
export function getCurrentPageViewState(): {
  url: string;
  path: string;
  title: string;
  referrer: string;
  startedAt: number;
  maxScrollPercent: number;
} {
  return {
    ...captureCurrentState(),
    startedAt: currentPageStart,
    maxScrollPercent: maxScrollSeen || getMaxScrollPercent(),
  };
}
